import { EdiDsnBilansBaseService } from './EdiDsnBilansBaseService';
import type { BilanDocumentContent, EdiDsnBilansServiceResult } from './EdiDsnBilansBaseService';

export class EdiDsnBilansFileService extends EdiDsnBilansBaseService {
  private readonly routeFileProperty = 'FileName';

  async tryFetchBilanFileAerContent(
    bilanInstanceId: string,
    signal?: AbortSignal,
  ): Promise<EdiDsnBilansServiceResult<Blob>> {
    const validationError = this.validateInstanceId(bilanInstanceId);
    if (validationError) {
      return validationError;
    }

    return this.fetchAerContent(
      `${encodeURIComponent(bilanInstanceId)}/${this.routeFileProperty}/AerContent`,
      signal,
    );
  }

  // One declaration can also return an archive containing several bilans.
  async tryFetchDsnBilansFileAerContent(
    declarationIds: readonly string[],
    signal?: AbortSignal,
  ): Promise<EdiDsnBilansServiceResult<Blob>> {
    if (!declarationIds.length || declarationIds.some(id => !id.trim())) {
      return { success: false, error: 'Les identifiants des déclarations sont requis' };
    }

    return this.fetchAerContent(
      `AerContent?declarationIds=${encodeURIComponent(declarationIds.join('|'))}`,
      signal,
    );
  }

  async tryFetchBilanFileRawContent(
    bilanInstanceId: string,
    signal?: AbortSignal,
  ): Promise<EdiDsnBilansServiceResult<Blob>> {
    const validationError = this.validateInstanceId(bilanInstanceId);
    if (validationError) {
      return validationError;
    }

    try {
      const apiFetchUrl = `${this.apiBaseUrl}/api/files/DsnBilans/${encodeURIComponent(bilanInstanceId)}/${this.routeFileProperty}/RawContent`;
      this.logger.info('Downloading bilan file content.');

      const response = await this.fetchGetQuery(apiFetchUrl, 'Bilan File Content', signal ?? null);
      const contentType = response.headers.get('content-type');
      const blob = await response.blob();

      this.logger.info('Downloaded bilan file content:', { contentType });

      return { success: true, data: blob, contentType };
    } catch (error) {
      this.logger.error('Error downloading bilan file content:', error);

      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erreur inconnue lors de la r\u00e9cup\u00e9ration du contenu du bilan',
      };
    }
  }

  async tryFetchBilanFileHtmlContent(
    bilanInstanceId: string,
    signal?: AbortSignal,
  ): Promise<EdiDsnBilansServiceResult<BilanDocumentContent>> {
    const validationError = this.validateInstanceId(bilanInstanceId);
    if (validationError) {
      return validationError;
    }

    try {
      const apiFetchUrl = `${this.apiBaseUrl}/api/files/DsnBilans/${encodeURIComponent(bilanInstanceId)}/${this.routeFileProperty}/HtmlContent?html-mode=full`;
      this.logger.info('Fetching bilan HTML content.');

      const response = await this.fetchGetQuery(apiFetchUrl, 'Bilan HTML Content', signal ?? null, [
        ['Accept', 'text/html'],
      ]);
      const contentType = response.headers.get('content-type');
      const content = await response.text();

      this.logger.info('Fetched bilan HTML content:', { contentType });

      return { success: true, data: { content }, contentType };
    } catch (error) {
      this.logger.error('Error fetching bilan HTML content:', error);

      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erreur inconnue lors de la r\u00e9cup\u00e9ration du contenu HTML du bilan',
      };
    }
  }

  protected override getUserFriendlyErrorMessage(status: number, method: string): string {
    // The AER controller returns 400 for both an invalid selection and no accessible bilan.
    if (status === 400 && method === 'AER Content') {
      return 'Aucun bilan AER disponible pour cette sélection, ou sélection invalide.';
    }

    return super.getUserFriendlyErrorMessage(status, method);
  }

  private async fetchAerContent(
    route: string,
    signal?: AbortSignal,
  ): Promise<EdiDsnBilansServiceResult<Blob>> {
    try {
      const apiFetchUrl = `${this.apiBaseUrl}/api/files/DsnBilans/${route}`;

      this.logger.info('Downloading AER file content.');

      const response = await this.fetchGetQuery(apiFetchUrl, 'AER Content', signal ?? null);
      const contentType = response.headers.get('content-type');
      const filename = response.headers.get('X-Filename');
      const mediaType = contentType?.split(';')[0].trim().toLowerCase();

      if (
        mediaType &&
        ![
          'application/octet-stream',
          'application/pdf',
          'application/zip',
          'application/x-zip-compressed',
        ].includes(mediaType)
      ) {
        return { success: false, error: 'Le serveur a renvoyé un contenu AER inattendu.' };
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        return { success: false, error: 'Aucun bilan AER disponible pour cette sélection.' };
      }

      return { success: true, data: blob, contentType, filename };
    } catch (error) {
      this.logger.error('Error downloading AER content.');
      return {
        success: false,
        error:
          error instanceof Error || error instanceof DOMException
            ? error.message
            : 'Erreur inconnue',
      };
    }
  }
}
